import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Loader2, Trash2 } from 'lucide-react';

const AdminMenu = () => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [menuType, setMenuType] = useState('images'); // 'images' or 'pdf'
  const [success, setSuccess] = useState(false);
  const [activeMenu, setActiveMenu] = useState({ type: 'none', urls: [] });
  const navigate = useNavigate();

  const fetchActiveMenu = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/menu`);
      if (response.ok) {
        const data = await response.json();
        setActiveMenu(data);
      }
    } catch (error) {
      console.error("Hiba az aktív étlap betöltésekor", error);
    }
  };

  useEffect(() => {
    fetchActiveMenu();
  }, []);

  const handleFileChange = (e) => {
    setSuccess(false);
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      
      if (menuType === 'pdf') {
        if (selectedFiles.length > 1) {
          toast.error('Kérlek csak 1 PDF fájlt válassz!');
          return;
        }
        if (selectedFiles[0].type !== 'application/pdf') {
          toast.error('Kérlek érvényes PDF fájlt válassz!');
          return;
        }
      } else {
        if (selectedFiles.length > 2) {
          toast.error('Kérlek maximum 2 képet válassz!');
          return;
        }
        for (let file of selectedFiles) {
          if (!file.type.startsWith('image/')) {
             toast.error('Kérlek érvényes kép formátumot (JPG, PNG) válassz!');
             return;
          }
        }
      }
      
      setFiles(selectedFiles);
    }
  };

  const handleFileSubmit = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      toast.error('Kérlek válassz ki fájlokat a feltöltéshez!');
      return;
    }

    setLoading(true);
    setSuccess(false);

    try {
      const token = localStorage.getItem('adminToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

      const formData = new FormData();
      formData.append('type', menuType);
      
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await fetch(`${backendUrl}/api/menu/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Hiba történt a fájl feltöltése során');
      }

      toast.success('Az étlap sikeresen frissítve!');
      setSuccess(true);
      setFiles([]);
      document.getElementById('menuFile').value = '';
      fetchActiveMenu();
      
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Hiba történt a mentés során.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMenu = async () => {
    if (!window.confirm("Biztosan törölni szeretnéd a jelenlegi étlapot?")) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

      const response = await fetch(`${backendUrl}/api/menu`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Hiba történt a törlés során');

      toast.success('Az étlap sikeresen törölve!');
      setActiveMenu({ type: 'none', urls: [] });
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Sikertelen törlés.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="container mx-auto max-w-4xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/admin/dashboard')} 
          className="mb-8 pl-0 hover:bg-transparent hover:text-accent font-sans text-xs tracking-widest text-muted-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Vissza a vezérlőpulthoz
        </Button>

        <h1 className="text-3xl font-serif font-bold text-primary mb-8">Étlap Kezelése</h1>

        <div className="grid md:grid-cols-2 gap-8">
          
          {/* Feltöltés Szekció */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-xl text-foreground">Étlap Feltöltése</CardTitle>
              <CardDescription>
                Tölts fel 1-2 képet, vagy egy PDF fájlt. A feltöltés azonnal felülírja a korábbit.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleFileSubmit} className="space-y-6">
                
                <div className="space-y-2">
                  <Label className="text-foreground">Fájl típusa</Label>
                  <Select onValueChange={(v) => { setMenuType(v); setFiles([]); setSuccess(false); document.getElementById('menuFile').value = ''; }} value={menuType}>
                    <SelectTrigger className="bg-background border-input text-foreground">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="images" className="cursor-pointer">Kép fájlok (Max 2 db, JPG/PNG)</SelectItem>
                      <SelectItem value="pdf" className="cursor-pointer">PDF Dokumentum</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="menuFile" className="text-foreground">Fájl(ok) kiválasztása</Label>
                  <Input
                    id="menuFile"
                    type="file"
                    multiple={menuType === 'images'}
                    accept={menuType === 'images' ? "image/jpeg, image/png, image/webp" : "application/pdf"}
                    onChange={handleFileChange}
                    className="bg-background text-foreground border-input file:mr-4 file:py-2 file:px-4 file:rounded-none file:border-0 file:text-xs file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                  />
                  {files.length > 0 && (
                     <div className="text-xs text-muted-foreground mt-2 font-medium bg-secondary/20 p-2 border border-border/50">
                       Kiválasztva: {files.map(f => f.name).join(', ')}
                     </div>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold uppercase tracking-widest text-xs h-12"
                  disabled={loading || files.length === 0}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  {loading ? 'Feltöltés...' : 'Étlap feltöltése'}
                </Button>

                {success && (
                  <div className="mt-4 p-4 border border-green-500/30 bg-green-500/10 rounded-none text-center">
                    <p className="text-sm font-medium text-green-600">Sikeres feltöltés!</p>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Aktív Étlap Előnézet */}
          <Card className="bg-card border-border flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-xl text-foreground">Aktív Étlap</CardTitle>
                <CardDescription>Jelenleg látható a weboldalon</CardDescription>
              </div>
              {activeMenu.type !== 'none' && (
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={handleDeleteMenu}
                  disabled={loading}
                  className="rounded-none uppercase tracking-widest text-[10px]"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Törlés
                </Button>
              )}
            </CardHeader>
            <CardContent className="flex-1 flex flex-col items-center justify-center bg-secondary/10 p-4 border-t border-border/40 min-h-[300px]">
               {activeMenu.type === 'none' || activeMenu.urls.length === 0 ? (
                 <p className="text-muted-foreground font-light text-center">Nincs feltöltve étlap.</p>
               ) : activeMenu.type === 'pdf' ? (
                 <div className="text-center">
                   <div className="text-4xl mb-4 text-accent">PDF</div>
                   <p className="text-sm font-medium text-foreground mb-2">1 PDF fájl aktív</p>
                   <a href={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${activeMenu.urls[0]}`} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground underline hover:text-accent">Megtekintés</a>
                 </div>
               ) : (
                 <div className="w-full flex flex-col gap-4">
                   <p className="text-sm font-medium text-foreground text-center mb-2">{activeMenu.urls.length} Kép aktív</p>
                   <div className="flex flex-wrap gap-2 justify-center">
                     {activeMenu.urls.map((url, idx) => (
                       <img 
                         key={idx} 
                         src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${url}`} 
                         alt="Előnézet" 
                         className="h-32 object-contain border border-border shadow-sm"
                       />
                     ))}
                   </div>
                 </div>
               )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default AdminMenu;
